/**
 * Operations → Settled book.
 *
 * The Settled book — cases that have settled and are being walked through
 * disbursement to Case Closed. This is the inverse of the Placed book: the
 * attention-heavy end of the pipeline, where the clock works against you and
 * checks have to reach the client, LF, and the OF per the FSA split before
 * the matter closes. A worklist, not a census.
 *
 * LAYOUT (portal-imprint, D1=hybrid, decision A): a portal-style <Table>. The
 * row keeps the worklist glance — a COMPACT disbursement strip (doc · HF · LF ·
 * OF, done/pending) — while the fee split and the full settlement detail move
 * to the detail panel (portal-faithful: uniform list rows, per-status detail in
 * the panel). The gate dates land in the panel's Settlement section in the
 * panel slice; the row carries done/pending today.
 *
 * LF-as-OF: when LegalFlare is itself the originating firm, the split is
 * two-party (HF + LegalFlare), so the OF receipt gate drops.
 *
 * `SettledCard` (below) is unchanged — The Surface renders settling matters
 * as that card in its mixed-phase overlay.
 *
 * Read-only. Cases enter (flip to Settled) and leave (close) upstream; the
 * surface re-derives on each poll.
 *
 * Spec: engineering/command-center/docs/port-notes/home-stretch.md (evicted 2026-08-29, #775 round 10)
 */

import { useState } from "react";
import { Check, Minus } from "lucide-react";
import type { SettledCase } from "@command-center/shared";
import { BookHeading } from "@/components/book-heading";
import { SearchBar, matchesQuery } from "@/components/search-bar";
import { SurfaceTrigger } from "@/components/surface-trigger";
import { CaseDetailPanel } from "@/components/case-detail-panel";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { settledToDetail, type CaseDetail } from "@/lib/case-detail";
import { fmtCurrency, fmtDate, fmtDuration } from "@/lib/format";
import { useSettled } from "@/lib/use-settled";

export function SettledPage() {
  const [query, setQuery] = useState("");
  const searching = query.trim() !== "";
  const settledBook = useSettled();
  // Lifted so a row stays open while you read it (portal parity: row → detail).
  const [detail, setDetail] = useState<CaseDetail | null>(null);

  if (settledBook.isLoading) {
    return <div className="p-8 text-sm text-plane-muted">Loading…</div>;
  }

  if (settledBook.isError) {
    return (
      <div className="p-8 text-sm text-error" data-testid="settled-error">
        Couldn't load the Settled book. It will retry automatically.
      </div>
    );
  }

  const cases = settledBook.data?.cases ?? [];
  const filteredCases = cases.filter((c) => matchesQuery(c.client, query));

  return (
    <section className="mx-auto max-w-4xl p-6" data-testid="settled">
      <BookHeading phaseKey="settled">
        <span
          className="text-sm text-plane-muted"
          data-testid="settled-count"
        >
          {cases.length === 0
            ? ""
            : `${cases.length} settling case${cases.length === 1 ? "" : "s"}`}
        </span>
      </BookHeading>

      <SearchBar value={query} onChange={setQuery} testId="settled-search" />

      {filteredCases.length === 0 ? (
        <div
          className="flex min-h-[50vh] items-center justify-center text-center"
          data-testid="settled-empty"
        >
          <p className="text-lg text-plane-muted">
            {searching ? "No matches." : "Nothing settling right now."}
          </p>
        </div>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="cursor-default hover:bg-transparent">
                <TableHead>Client</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Handling firm</TableHead>
                <TableHead>Settling</TableHead>
                <TableHead>Disbursement</TableHead>
                <TableHead>
                  <span className="sr-only">Watch</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCases.map((c) => (
                <SettledTableRow
                  key={c.id}
                  settlingCase={c}
                  onOpenDetail={() => setDetail(settledToDetail(c))}
                />
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {detail ? (
        <CaseDetailPanel
          key={
            detail.actionTarget?.placementId ??
            detail.leadActionTarget?.leadId ??
            detail.name
          }
          detail={detail}
          onClose={() => setDetail(null)}
          testId="settled-summary-panel"
        />
      ) : null}
    </section>
  );
}

/** One settling case as a portal-style table row. The compact disbursement
 *  strip is the worklist glance (done/pending per gate); the amount and the
 *  days-settling are scannable columns. The fee split + full settlement detail
 *  open in the panel. The OF receipt gate drops when LegalFlare is the OF. */
function SettledTableRow({
  settlingCase: c,
  onOpenDetail,
}: {
  settlingCase: SettledCase;
  onOpenDetail: () => void;
}) {
  return (
    <TableRow onClick={onOpenDetail} data-testid="settled-card">
      <TableCell className="font-medium">{c.client}</TableCell>
      <TableCell className="tabular-nums" data-testid="settled-amount">
        {c.settlementAmount !== null
          ? fmtCurrency(c.settlementAmount)
          : "Amount not reported"}
      </TableCell>
      <TableCell className={c.handlingFirmName ? undefined : "text-muted-foreground"}>
        {c.handlingFirmName ?? "—"}
      </TableCell>
      <TableCell className="tabular-nums" data-testid="settled-age">
        {c.daysInSettled !== null ? fmtDuration(c.daysInSettled) : "—"}
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
          <Gate label="Doc" done={c.hasSettlementDocument} testid="settled-doc" />
          <Gate
            label="HF"
            done={c.hfDisbursementConfirmed != null}
            testid="settled-hf"
          />
          <Gate label="LF" done={c.lfPaymentDate != null} testid="settled-lf" />
          {!c.originatingFirmIsLegalFlare ? (
            <Gate label="OF" done={c.ofPaymentDate != null} testid="settled-of" />
          ) : null}
        </div>
      </TableCell>
      <TableCell className="w-0 text-right" onClick={(e) => e.stopPropagation()}>
        <SurfaceTrigger leadId={c.leadRecordId} />
      </TableCell>
    </TableRow>
  );
}

/** One settling case as a card — used by The Surface's mixed-phase overlay.
 *  who + amount, how long it's been settling, the parties (firms + fee split),
 *  and the disbursement gates. The LF line and OF gate drop when LF is the OF. */
export function SettledCard({
  settlingCase: c,
  onOpenDetail,
}: {
  settlingCase: SettledCase;
  /** When provided (on The Surface), a "Case detail →" link opens the panel. */
  onOpenDetail?: () => void;
}) {
  return (
    <li
      className="rounded-lg border bg-card text-card-foreground px-4 py-3"
      data-testid="settled-card"
    >
      {/* Top line: who + money on the left, days-in-Settled on the right. */}
      <div className="flex items-start gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-sm font-medium">{c.client}</span>
          <span
            className="text-xs tabular-nums text-muted-foreground"
            data-testid="settled-amount"
          >
            {c.settlementAmount !== null
              ? fmtCurrency(c.settlementAmount)
              : "Amount not reported"}
          </span>
        </div>
        <span className="shrink-0 text-right">
          <span
            className="text-sm font-semibold tabular-nums"
            data-testid="settled-age"
          >
            {c.daysInSettled !== null ? fmtDuration(c.daysInSettled) : "—"}
          </span>
          <span className="block text-[11px] text-muted-foreground">
            in settled
          </span>
        </span>
        <SurfaceTrigger leadId={c.leadRecordId} />
      </div>

      {/* The parties: originating + handling firm and the fee split. LF takes
          no separate line when it is itself the OF (two-party split). */}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <Party
          role="OF"
          name={c.originatingFirmName}
          pct={c.ofFeePct}
          testid="settled-of-firm"
        />
        <Party
          role="HF"
          name={c.handlingFirmName}
          pct={c.hfFeePct}
          testid="settled-hf-firm"
        />
        {!c.originatingFirmIsLegalFlare ? (
          <Party role="LF" name={null} pct={c.lfFeePct} testid="settled-lf-firm" />
        ) : null}
      </div>

      {/* The disbursement spine — discrete gates, no progress bar. The OF
          receipt gate drops when LegalFlare is the OF (no third-party OF). */}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]">
        <Gate
          label="Itemized doc"
          done={c.hasSettlementDocument}
          testid="settled-doc"
        />
        <Gate label="HF disbursed" date={c.hfDisbursementConfirmed} testid="settled-hf" />
        <Gate label="LF received" date={c.lfPaymentDate} testid="settled-lf" />
        {!c.originatingFirmIsLegalFlare ? (
          <Gate label="OF received" date={c.ofPaymentDate} testid="settled-of" />
        ) : null}
      </div>

      {onOpenDetail ? (
        <button
          type="button"
          onClick={onOpenDetail}
          className="mt-2 text-xs font-medium text-interactive hover:underline"
          data-testid="settled-open-detail"
        >
          Case detail →
        </button>
      ) : null}
    </li>
  );
}

/**
 * One party in the fee split — a role chip (OF / HF / LF), the firm name
 * (LF has none — it is the platform), and the share percentage. Pct/name are
 * shown only when present.
 */
function Party({
  role,
  name,
  pct,
  testid,
}: {
  role: "OF" | "HF" | "LF";
  name: string | null;
  pct: number | null;
  testid: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={testid}>
      <span className="rounded bg-muted px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {role}
      </span>
      {name ? <span className="font-medium">{name}</span> : null}
      {pct != null ? (
        <span className="tabular-nums text-muted-foreground">{fmtPct(pct)}</span>
      ) : null}
    </span>
  );
}

/**
 * One disbursement gate — a labeled check. Pass `done` for the boolean doc
 * gate (or the compact row strip), or `date` for the HF/LF/OF marks (a present
 * date means confirmed, and is shown as the confirmation date). Confirmed reads
 * in foreground with a green check; pending is muted with a dash.
 */
function Gate({
  label,
  done,
  date,
  testid,
}: {
  label: string;
  done?: boolean;
  date?: string | null;
  testid: string;
}) {
  const isDone = done ?? date != null;
  return (
    <span
      className="inline-flex items-center gap-1"
      data-testid={testid}
      data-done={isDone}
    >
      {isDone ? (
        <Check className="h-3 w-3 shrink-0 text-success" />
      ) : (
        <Minus className="h-3 w-3 shrink-0 text-muted-foreground/40" />
      )}
      <span className={isDone ? "text-foreground" : "text-muted-foreground"}>
        {label}
      </span>
      {date != null ? (
        <span className="tabular-nums text-muted-foreground">{fmtDate(date)}</span>
      ) : null}
    </span>
  );
}

/** Fee share as a whole percent — 0.6 → "60%". */
function fmtPct(pct: number): string {
  return `${Math.round(pct * 100)}%`;
}
